import { networkKey } from './postLength';
import { kindOfPost } from './composerRules';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { HistoryLink, HistoryView } from '../cms/HistoryView';
import { showInkToast } from '../cms/InkToast';
import { useAddons } from '../cms/addons';
import { track } from '../../lib/usage';
import { useChannelConnect } from './ChannelPicker';
import { reconnectWords } from '../cms/missedPost';
import { WritePost } from './WritePost';
import { useVoiceRewrite } from './useVoiceRewrite';
import {
  checkRows,
  foldRow,
  ItemCheck,
  itemRows,
  KEEP_VOICE_WORDING,
  SOCIAL_POST_CHECK,
  VOICE_PATH,
  VoiceCheck,
  VoiceRow,
} from './voiceWords';
import {
  Channel,
  channelKind,
  channelMark,
  channelName,
  dayTime,
  goneStatus,
  isPersonalProfile,
  LINK_SOCIAL_POST_TO_SITE,
  localZone,
  networkLink,
  postText,
  postTime,
  REACH_CAMPAIGN_NAMES,
  REACH_CHANNELS,
  REACH_POST_LINK,
  ReachPost,
  time,
  needsReconnect,
} from './reach';
import { POST_STATUS } from '../statusWords';

/**
 * What the panel can do to a post. `at` is the picked time for 'schedule'; a
 * reason goes with 'approve' for a post held by its item checks.
 */
export type PostAction = 'approve' | 'skip' | 'retry' | 'post_now' | 'schedule' | 'discard';

const inputValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const TONE: Record<string, { dot: string; border: string; label: string; ink: string }> = {
  posted: {
    dot: 'var(--ink-green)',
    border: 'var(--ink-line)',
    label: POST_STATUS.posted,
    ink: 'var(--ink-green-ink)',
  },
  publishing: {
    dot: 'var(--ink-green)',
    border: 'var(--ink-line)',
    label: POST_STATUS.publishing,
    ink: 'var(--ink-green-ink)',
  },
  scheduled: {
    dot: 'var(--ink-accent)',
    border: 'var(--ink-line)',
    label: POST_STATUS.scheduled,
    ink: 'var(--ink-accent)',
  },
  waiting: {
    dot: 'var(--ink-amber)',
    border: 'var(--ink-amber)',
    label: POST_STATUS.waiting,
    ink: 'var(--ink-amber)',
  },
  // A post whose time passed without going out (Prototype v7: "Didn't post").
  missed: {
    dot: 'var(--ink-red)',
    border: 'var(--ink-red)',
    label: POST_STATUS.missed,
    ink: 'var(--ink-red)',
  },
  // Saved without a time: nothing goes out until it is given one.
  draft: {
    dot: 'var(--ink-ink3)',
    border: 'var(--ink-line2)',
    label: POST_STATUS.draft,
    ink: 'var(--ink-ink3)',
  },
  skipped: {
    dot: 'var(--ink-ink3)',
    border: 'var(--ink-line2)',
    label: POST_STATUS.skipped,
    ink: 'var(--ink-ink3)',
  },
  // On its way down from its network, then in Social › Posts › Deleted.
  deleting: {
    dot: 'var(--ink-ink3)',
    border: 'var(--ink-line2)',
    label: POST_STATUS.deleting,
    ink: 'var(--ink-ink3)',
  },
  deleted: {
    dot: 'var(--ink-ink3)',
    border: 'var(--ink-line2)',
    label: POST_STATUS.deleted,
    ink: 'var(--ink-ink3)',
  },
};

const initials = (name: string) =>
  name
    .replace(/^@/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

/** "montecarlo.tinycms.ai/go/fall-guide" for the link card. */
const shortUrl = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/** A title from the address when nothing on the site matches it. */
function titleFromUrl(url: string) {
  try {
    const u = new URL(/^https?:/i.test(url) ? url : `https://${url}`);
    const last =
      u.pathname
        .replace(/^\/|\/$/g, '')
        .split('/')
        .pop() || u.host;
    const t = decodeURIComponent(last).replace(/[-_]+/g, ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
  } catch {
    return url;
  }
}

type LinkTarget = {
  kind: string;
  id?: string | null;
  title?: string | null;
  url: string;
  /** The link leaves the site (UX review round 9). */
  offSite?: boolean;
  /** The same article on the site, for "Use your page instead". */
  ownUrl?: string | null;
};

/** "spacewalk-preview.jpg": the picture's own name for the Image row. */
function imageName(url: string) {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop() || '';
    const name = decodeURIComponent(last);
    return name.length > 48 ? `${name.slice(0, 45)}…` : name || 'Image';
  } catch {
    return 'Image';
  }
}

/**
 * One post, opened from the Calendar's week or from a channel's "Last post
 * didn't go out · Fix" (Prototype v15 "Reach post"): the post as it shows on
 * its channel — who posts it, the words, the link card — then its campaign,
 * what it links to and its picture, a line on what to do, and the actions its
 * state allows. A post that went out has no actions; one in a campaign's plan
 * is approved and changed with its campaign.
 */
/**
 * The Reach side panel (UX optimization review, 2026-10-24): anything already
 * on the calendar or a campaign board opens here, whatever its status. Edit
 * swaps in the composer's own fields and buttons (WritePost variant "panel";
 * the channel stays); closing drops unsaved edits. A post that hasn't gone out
 * (scheduled, waiting, failed) has a trash button that asks in a red strip.
 */
export function PostPanel({
  post,
  busy,
  onClose,
  onAct,
  onDelete,
  onSaved,
  onReconnected,
  approver = null,
  channelList = null,
  campaignList = null,
}: {
  post: ReachPost;
  busy: boolean;
  onClose: () => void;
  onAct: (kind: PostAction, at?: Date, reason?: string) => void;
  /** Delete post (postActions.ts remove): closes the panel, toast with Undo. */
  onDelete?: () => void;
  /** After Edit → Save: close and refresh. */
  onSaved?: () => unknown;
  /** A Reconnect from the panel worked: the post's reason has changed. */
  onReconnected?: () => unknown;
  /** The Campaign role: who approves (their saves wait for that person). */
  approver?: string | null;
  channelList?: Channel[] | null;
  campaignList?: React.ComponentProps<typeof WritePost>['campaignList'];
}) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyChanged, setHistoryChanged] = useState(false);
  const navigate = useNavigate();
  const { accountId } = useAccount();
  const [reconnected, setReconnected] = useState(false);
  const lapsed = needsReconnect(post);
  const { data: channelRows } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !lapsed || !accountId,
    fetchPolicy: 'cache-first',
  });
  const { start: startSignIn, picker } = useChannelConnect({
    accountId,
    onConnected: () => {
      setReconnected(true);
      showInkToast(`${channelName(post.platform)} reconnected. You can try again now.`);
      void onReconnected?.();
    },
  });
  const startReconnect = () => {
    const platform = (post.platform || '').toLowerCase().replace(/^twitter$/, 'x');
    // Bluesky signs in with an app password, on its Channels row.
    if (platform === 'bluesky') {
      navigate('/reach/channels');
      return;
    }
    const row = (channelRows?.socialProfiles || channelList || []).find(
      (c) => c.id === post.socialProfileId
    );
    void startSignIn(platform, row?.profileId ? { platform, profileId: row.profileId } : undefined);
  };
  const [picking, setPicking] = useState(false);
  const [at, setAt] = useState(() => inputValue(new Date(Date.now() + 60 * 60 * 1000)));
  const atOk = new Date(at).getTime() > Date.now() + 60 * 1000;
  // A deleted post is only there to read, whatever it was before.
  const status = goneStatus(post) || post.reachStatus;
  const tone = TONE[status] || TONE.scheduled;
  const t = postTime(post);
  const channel = channelName(post.platform);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Who posts it, its campaign's name, and what its link opens (by name).
  // From the network too: a sign-in since this session loaded can change
  // canDeletePosts, or add the channel, and connecting only refreshes the Channels list.
  const { data: chData } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: campData } = useQuery<{ projects: Array<{ id: string; name: string }> }>(
    REACH_CAMPAIGN_NAMES,
    { variables: { accountId }, skip: !accountId || !post.projectId, fetchPolicy: 'cache-first' }
  );
  const { data: linkData } = useQuery<{ reachPostLink: LinkTarget | null }>(REACH_POST_LINK, {
    variables: { postId: post.id },
    skip: !post.postUrl,
    fetchPolicy: 'cache-and-network',
  });

  // The stored check, read fresh: one still running shows up without reopening.
  const checkQ = useQuery<{
    socialPost: { id: string; voiceCheck: VoiceCheck | null; itemCheck: ItemCheck | null } | null;
  }>(SOCIAL_POST_CHECK, { variables: { id: post.id }, fetchPolicy: 'cache-and-network' });
  const check: VoiceCheck | null = checkQ.data?.socialPost
    ? checkQ.data.socialPost.voiceCheck
    : post.voiceCheck || null;
  const items: ItemCheck | null = checkQ.data?.socialPost
    ? checkQ.data.socialPost.itemCheck
    : post.itemCheck || null;
  const showVoice = Boolean(check && check.state !== 'pro');
  const { isOwner } = useAddons();
  const [reason, setReason] = useState<string | null>(null);
  const checkPending = check?.state === 'pending';
  const { startPolling, stopPolling } = checkQ;
  useEffect(() => {
    if (checkPending) startPolling(4000);
    else stopPolling();
    return () => stopPolling();
  }, [checkPending, startPolling, stopPolling]);
  const { rewrite, rewriting } = useVoiceRewrite(onSaved);
  const [keepWording, { loading: keeping }] = useMutation(KEEP_VOICE_WORDING);
  const useRewrite = () => void rewrite([{ kind: 'post', id: post.id }]);
  const keepMine = async () => {
    try {
      await keepWording({ variables: { kind: 'post', id: post.id } });
      await checkQ.refetch();
      showInkToast('Kept your wording.');
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const profile = chData?.socialProfiles.find((c) => c.id === post.socialProfileId) || null;
  const personal = profile ? isPersonalProfile(profile.platform, profile.profileId) : false;
  const who = profile?.profileName || profile?.profileUsername || channel;
  // A handle gets its @; a Facebook Page's name ("Space Interests") isn't one.
  const username = profile?.profileUsername?.trim() || '';
  const handle = personal
    ? 'Personal profile, not a Page'
    : username && !/\s/.test(username)
      ? `@${username.replace(/^@/, '')}`
      : channelKind(post.platform, profile?.profileId);
  // Out on the network, and where: nothing to open before it's posted.
  const postsTo = profile ? `${who} on ${channel}` : channel;
  const profileUrl = networkLink(profile?.profileLink);
  const liveUrl = status === 'posted' ? networkLink(post.livePostUrl) : null;
  const campaign = post.projectId
    ? campData?.projects.find((c) => c.id === post.projectId) || null
    : null;
  const target = linkData?.reachPostLink || null;
  const link = post.postUrl?.trim() || '';
  // Round 9: a post that sends readers off the site says so, and can use the site's page.
  const offDomain = post.offSiteDomain || (target?.offSite ? shortUrl(link).split('/')[0] : null);
  const [linkToSite, { loading: relinking }] = useMutation(LINK_SOCIAL_POST_TO_SITE);
  const canRelink =
    Boolean(target?.ownUrl) && ['scheduled', 'waiting', 'missed', 'draft'].includes(status);
  const relink = async () => {
    try {
      await linkToSite({ variables: { id: post.id }, refetchQueries: [REACH_POST_LINK] });
      showInkToast('Now links to the article on your site.');
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };
  const linkTitle = target?.title?.trim() || (link ? titleFromUrl(link) : '');
  const openLink = () => {
    if (target?.kind === 'article' && target.id) navigate(`/content/${target.id}`);
    else if (target?.kind === 'page' && target.id)
      navigate(`/content?view=pages&page=${target.id}`);
    else if (link) window.open(link, '_blank', 'noopener,noreferrer');
  };
  const openCampaign = () => post.projectId && navigate(`/campaigns?campaign=${post.projectId}`);

  const missed = status === 'missed';
  const waiting = status === 'waiting';
  const scheduled = status === 'scheduled';
  const draft = status === 'draft';
  const done = ['posted', 'publishing', 'skipped', 'deleting', 'deleted'].includes(status);
  // One of a campaign plan's posts: approved, and changed, with its campaign.
  const inPlan = Boolean(post.projectPostId);
  const kind = kindOfPost(post);
  const isVideo = kind === 'video';
  const image = isVideo ? '' : post.imageUrl || '';
  const photo = Boolean(image) && kind !== 'link';
  const videoUrl = isVideo ? post.imageUrl || '' : '';

  const reconnect = needsReconnect(post) && !reconnected;
  const note = reconnect
    ? reconnectWords(channel)
    : post.waitsForMedia && !done
      ? 'The video is still preparing. It goes out as soon as it’s ready.'
      : missed
        ? `${channel} didn’t take this post${t ? ` at ${time(t)}` : ''}${
            reconnected ? '' : post.failure ? `: ${post.failure}` : ''
          }${reconnected ? ', and it’s reconnected now' : ''}. Nothing was posted.`
        : waiting && inPlan
          ? 'This post is part of a campaign. Approve the campaign once and all of its posts are scheduled.'
          : done || draft
            ? ''
            : post.createdVia === 'app'
              ? 'Want different wording? Edit it here. It keeps its time.'
              : 'Want different wording? Ask your agent. It redrafts and the new version comes back here.';

  const edit = inPlan ? openCampaign : () => setEditing(true);
  const held = Boolean(check?.holds);
  // Text that looks like a key: fixed in Edit, or approved anyway with a reason.
  const keyHeld = Boolean(items?.holds);
  const fold = items ? foldRow(items) : null;
  const checkRow = (row: VoiceRow) => (
    <div className="vs-check" key={row.text}>
      <span style={{ color: row.color, fontWeight: 600 }}>{row.icon}</span>
      <span>
        {row.text}
        {row.set ? (
          <>
            {' · '}
            <button type="button" className="ink-quiet-link" onClick={() => navigate(VOICE_PATH)}>
              Set one
            </button>
          </>
        ) : null}
      </span>
      <span className="vs-check-sev" style={{ color: row.color }}>
        {row.sev}
      </span>
    </div>
  );
  // A campaign plan's post changes in its campaign, not here.
  const canRewrite = Boolean(check?.rewrite) && !check?.overLimit && !inPlan && !done;
  const voiceBusy = busy || rewriting || keeping;
  // A live post comes down from its channel: Threads, X, Bluesky, YouTube, and an
  // Instagram account whose Facebook sign-in allows it. TikTok's API can't delete a post.
  const deletesLive =
    status === 'posted' &&
    (post.platform === 'threads' ||
      post.platform === 'x' ||
      post.platform === 'bluesky' ||
      post.platform === 'youtube' ||
      (post.platform === 'instagram' && Boolean(profile?.canDeletePosts)));
  // Delete: anything that hasn't gone out, except a draft (it has Delete draft).
  const canDelete = Boolean(onDelete) && (missed || waiting || scheduled || deletesLive);
  const campaignWords = campaign?.name ? `It comes out of ${campaign.name} and` : 'It';
  const trash = canDelete ? (
    <button
      type="button"
      className="rc-panel-trash"
      title="Delete post"
      aria-label="Delete post"
      disabled={busy}
      onClick={() => setConfirmDelete(true)}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
      </svg>
    </button>
  ) : null;
  return (
    <>
      <div className="rc-panel-back" onClick={onClose} />
      <aside className="rc-panel" role="dialog" aria-label={`${channel} post`}>
        <div className="rc-panel-head">
          <span className="rc-mark rc-panel-mark">{channelMark(post.platform)}</span>
          <div className="rc-panel-title">
            <strong>{channel}</strong>
            <span className="rc-panel-status" style={{ color: tone.ink }}>
              <span className="ink-dot" style={{ background: tone.dot }} />
              {waiting && approver ? `Waiting on ${approver}` : tone.label}
              {t ? <span className="rc-panel-when">· {dayTime(t)}</span> : null}
            </span>
          </div>
          <button type="button" className="rc-panel-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {editing ? (
          <WritePost
            variant="panel"
            draft={post}
            approver={approver}
            channelList={channelList}
            campaignList={campaignList}
            onClose={() => setEditing(false)}
            onPosted={() => {
              setEditing(false);
              void onSaved?.();
            }}
          />
        ) : (
          <>
            <div className="rc-panel-body">
              {/* The post as it shows on its channel. */}
              <div className="rc-panel-card">
                <div className="rc-panel-who">
                  <span className="rc-panel-avatar">{initials(who)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="rc-panel-who-name">{who}</div>
                    <div className="rc-panel-who-handle">{handle}</div>
                  </div>
                  {liveUrl ? (
                    <a
                      className="rc-panel-viewon"
                      href={liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on {channel}
                    </a>
                  ) : null}
                </div>
                <div className="rc-panel-text">{postText(post) || 'Untitled post'}</div>
                {photo ? <img className="rc-panel-photo" src={image} alt="" /> : null}
                {videoUrl ? (
                  <video
                    className="rc-panel-photo"
                    src={videoUrl}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : null}
                {isVideo && networkKey(post.platform) === 'youtube' && post.title ? (
                  <div className="rc-panel-text">
                    <strong>{post.title}</strong>
                  </div>
                ) : null}
                {link && kind === 'link' ? (
                  <div className="rc-panel-linkcard">
                    {image ? (
                      <img className="rc-panel-linkimg" src={image} alt="" />
                    ) : (
                      <div className="rc-panel-linkimg rc-panel-linkimg-empty">
                        link preview image
                      </div>
                    )}
                    <div className="rc-panel-linktext">
                      <div className="rc-panel-domain">{shortUrl(link)}</div>
                      <div className="rc-panel-linktitle">{linkTitle}</div>
                    </div>
                  </div>
                ) : null}
              </div>

              {showVoice || items ? (
                <div className="rc-checks">
                  <div className="rc-checks-head">
                    <span>Checks</span>
                    {showVoice ? (
                      <button
                        type="button"
                        className="ink-quiet-link"
                        onClick={() => navigate(VOICE_PATH)}
                      >
                        Voice &amp; style
                      </button>
                    ) : null}
                  </div>
                  {items ? itemRows(items).map(checkRow) : null}
                  {check && showVoice ? checkRows(check).map(checkRow) : null}
                  {check && showVoice && check.state === 'findings' && !check.kept && !done ? (
                    check.rewrite ? (
                      <div className="rc-voice-fix">
                        <div className="rc-voice-fix-head">IN YOUR VOICE</div>
                        <div className="rc-voice-fix-text">{check.rewrite}</div>
                        {check.overLimit ? (
                          <div className="rc-voice-fix-over">
                            This is over the limit on {channel}, so it isn’t saved for you. Edit the
                            post to fit.
                          </div>
                        ) : null}
                        <div className="rc-voice-fix-actions">
                          {canRewrite ? (
                            <button
                              type="button"
                              className="ink-btn ink-btn-primary"
                              disabled={voiceBusy}
                              onClick={useRewrite}
                            >
                              {rewriting ? 'Saving…' : 'Use this version'}
                            </button>
                          ) : null}
                          {held ? null : (
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={voiceBusy}
                              onClick={() => void keepMine()}
                            >
                              Keep mine
                            </button>
                          )}
                        </div>
                      </div>
                    ) : held ? null : (
                      <div className="rc-voice-fix-actions">
                        <button
                          type="button"
                          className="ink-btn"
                          disabled={voiceBusy}
                          onClick={() => void keepMine()}
                        >
                          Keep mine
                        </button>
                      </div>
                    )
                  ) : null}
                  {fold ? checkRow(fold) : null}
                </div>
              ) : null}

              <div className="rc-panel-rows">
                <div className="rc-panel-row">
                  <span>Posts to</span>
                  {profileUrl ? (
                    <a
                      className="rc-panel-rowlink"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {postsTo}
                    </a>
                  ) : (
                    <span>{postsTo}</span>
                  )}
                </div>
                {post.projectId ? (
                  <div className="rc-panel-row">
                    <span>Campaign</span>
                    <button type="button" className="rc-panel-rowlink" onClick={openCampaign}>
                      {campaign?.name || 'Open the campaign'}
                    </button>
                  </div>
                ) : null}
                {link ? (
                  <div className="rc-panel-row">
                    <span>Links to</span>
                    {offDomain ? (
                      <button
                        type="button"
                        className="rc-panel-rowlink rc-panel-offsite"
                        onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                      >
                        {shortUrl(link)} (not your site)
                      </button>
                    ) : (
                      <button type="button" className="rc-panel-rowlink" onClick={openLink}>
                        {linkTitle}
                      </button>
                    )}
                  </div>
                ) : null}
                <div className="rc-panel-row">
                  <span>{isVideo ? 'Video' : 'Image'}</span>
                  <span>
                    {isVideo
                      ? post.waitsForMedia
                        ? 'Preparing'
                        : imageName(videoUrl) || 'Video'
                      : image
                        ? imageName(image)
                        : link
                          ? 'Link preview'
                          : 'None'}
                  </span>
                </div>
                <div className="rc-panel-row">
                  <span>History</span>
                  <HistoryLink
                    label="Versions and approvals →"
                    onOpen={() => setHistoryOpen(true)}
                  />
                </div>
              </div>
              {offDomain ? (
                <div className="rc-panel-offsite-warn" role="note">
                  <span>
                    This post links to <strong>{offDomain}</strong>, not your site. Readers who
                    click leave your site.
                  </span>
                  {canRelink ? (
                    <button
                      type="button"
                      className="ink-quiet-link"
                      disabled={relinking}
                      onClick={() => void relink()}
                    >
                      Use your page instead
                    </button>
                  ) : null}
                </div>
              ) : null}

              {note ? <p className="rc-panel-why">{note}</p> : null}

              {draft && picking ? (
                <div className="rc-panel-pick">
                  <input
                    className="ink-site-input"
                    type="datetime-local"
                    value={at}
                    min={inputValue(new Date())}
                    onChange={(e) => setAt(e.target.value)}
                    aria-invalid={!atOk}
                    aria-label="When it goes out"
                  />
                  <span className="ink-muted" style={{ fontSize: 12 }}>
                    Your time zone: {localZone()}
                  </span>
                </div>
              ) : null}
            </div>
            {draft ? (
              <div className="rc-panel-foot">
                <button
                  type="button"
                  className="ink-btn ink-btn-quiet"
                  disabled={busy}
                  onClick={() => onAct('discard')}
                >
                  Delete draft
                </button>
                {inPlan ? null : (
                  <button
                    type="button"
                    className="ink-btn"
                    disabled={busy}
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                )}
                {picking ? (
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary rc-panel-primary"
                    disabled={busy || !atOk}
                    onClick={() => onAct('schedule', new Date(at))}
                  >
                    Schedule
                  </button>
                ) : held ? (
                  canRewrite ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary rc-panel-primary"
                      disabled={voiceBusy}
                      onClick={useRewrite}
                    >
                      Use the version in your voice
                    </button>
                  ) : null
                ) : (
                  <>
                    <button
                      type="button"
                      className="ink-btn"
                      disabled={busy}
                      onClick={() => onAct('post_now')}
                    >
                      Post now
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary rc-panel-primary"
                      disabled={busy}
                      onClick={() => setPicking(true)}
                    >
                      Pick a time
                    </button>
                  </>
                )}
              </div>
            ) : !done && reason !== null ? (
              <form
                className="rc-panel-foot rc-panel-reason"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!reason.trim()) return;
                  track('held_override', { label: 'admin' });
                  onAct('approve', undefined, reason.trim());
                }}
              >
                <input
                  className="ink-site-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why it can go out as it is"
                  aria-label="Reason to approve anyway"
                  autoFocus
                />
                <span className="rc-panel-reason-help">
                  Recorded with your approval. Leave the password or key out.
                </span>
                <button
                  type="button"
                  className="ink-btn"
                  disabled={busy}
                  onClick={() => setReason(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ink-btn ink-btn-primary rc-panel-primary"
                  disabled={busy || !reason.trim()}
                >
                  Approve anyway
                </button>
              </form>
            ) : (!done || deletesLive) && confirmDelete ? (
              <div
                className="rc-panel-foot rc-panel-delete"
                role="alertdialog"
                aria-label="Delete post"
              >
                <span className="rc-panel-delete-text">
                  {deletesLive
                    ? `Delete this post from ${channel}? It comes down for everyone, and you can undo right after.`
                    : post.projectId && campaign?.name
                      ? `Delete this post? ${campaignWords} won’t go out. Nothing is posted.`
                      : 'Delete this post? It won’t go out, and you can undo right after.'}
                </span>
                <button
                  type="button"
                  className="ink-btn"
                  disabled={busy}
                  onClick={() => setConfirmDelete(false)}
                >
                  Keep it
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-danger"
                  disabled={busy}
                  onClick={() => onDelete?.()}
                >
                  Delete post
                </button>
              </div>
            ) : deletesLive && canDelete ? (
              <div className="rc-panel-foot">{trash}</div>
            ) : !done ? (
              <div className="rc-panel-foot">
                {trash}
                {/* A campaign plan's waiting post is skipped with its campaign. */}
                {missed || scheduled || (waiting && !inPlan) ? (
                  <button
                    type="button"
                    className="ink-btn"
                    disabled={busy}
                    onClick={() => onAct('skip')}
                  >
                    {scheduled ? 'Don’t post' : 'Skip'}
                  </button>
                ) : null}
                {edit ? (
                  <button type="button" className="ink-btn" disabled={busy} onClick={edit}>
                    Edit
                  </button>
                ) : null}
                {/* The Campaign role can't approve: that waits for the site's approver. */}
                {keyHeld && !inPlan && (missed || (waiting && !approver)) ? (
                  missed ? null : isOwner ? (
                    <button
                      type="button"
                      className="ink-btn rc-panel-primary"
                      disabled={busy}
                      onClick={() => setReason('')}
                    >
                      Approve anyway, with a reason
                    </button>
                  ) : (
                    <span className="ink-held-note">
                      Only owners and admins can approve held items.
                    </span>
                  )
                ) : (missed || (waiting && !approver)) && held && !inPlan ? (
                  canRewrite ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary rc-panel-primary"
                      disabled={voiceBusy}
                      onClick={useRewrite}
                    >
                      Use the version in your voice
                    </button>
                  ) : null
                ) : reconnect ? (
                  <span className="rc-panel-primary rc-panel-reconnect">
                    <button
                      type="button"
                      className="ink-btn"
                      aria-disabled="true"
                      title="Reconnect first"
                      data-off="true"
                      onClick={() => showInkToast('Reconnect first')}
                    >
                      Try again
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={busy}
                      onClick={startReconnect}
                    >
                      Reconnect {channel}
                    </button>
                  </span>
                ) : missed || (waiting && !approver) ? (
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary rc-panel-primary"
                    disabled={busy}
                    onClick={() =>
                      missed ? onAct('retry') : inPlan ? openCampaign() : onAct('approve')
                    }
                  >
                    {missed ? 'Try again' : inPlan ? 'Review campaign' : 'Approve'}
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </aside>
      {picker}
      {historyOpen ? (
        <HistoryView
          kind="post"
          id={post.id}
          onClose={() => {
            setHistoryOpen(false);
            // Refreshing closes this panel, so it waits until History does.
            if (historyChanged) void onSaved?.();
          }}
          onChanged={() => setHistoryChanged(true)}
        />
      ) : null}
    </>
  );
}
